import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../axiosInstance";

/**
 * Custom hook to fetch assessment reviews (approved, rejected, pending) for the logged-in assessment reviewer.
 * Returns data arrays, summary counts, loading & error states and a refetch method.
 */
const useMyCommitteeReviews = () => {
  const [pendingReviews, setPendingReviews] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [rejectedReviews, setRejectedReviews] = useState([]);
  const [pricingRequestsProgress, setPricingRequestsProgress] = useState([]);
  const [raRequestsProgress, setRaRequestsProgress] = useState([]);
  const [summary, setSummary] = useState({
    approved_count: 0,
    rejected_count: 0,
    pending_count: 0,
    pricing_count: 0,
    ra_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.get(
        "/api/committee-reviews/my-reviews",
        { withCredentials: true }
      );

      const data = response?.data || {};
      // Transform approved and rejected objects to match requestProgress shape used by UI
      const transformReview = (item) => {
        if (!item) return item;
        // Some endpoints send nested requests_progress, others may send requestProgress directly
        const rp = item.requests_progress || item.request_progress || {};
        const transformed = {
          ...rp,
          // Ensure Stage property exists regardless of naming (stage vs Stage)
          Stage: rp.Stage || rp.stage || rp.Stage || {},
          status: item.review || rp.status || "approved", // fallback
        };
        return transformed;
      };

      const approvedArr = (data.approved || []).map((it) => {
        const transformed = transformReview(it);
        transformed.status = "approved"; // override for safety (same pattern as rejected)
        // Add approval source (who approved: me, core, both)
        if (it.approvalSource) {
          transformed.approvalSource = it.approvalSource;
        }
        return transformed;
      });
      const rejectedArr = (data.rejected || []).map((it) => {
        const transformed = transformReview(it);
        transformed.status = "rejected"; // override for safety
        // Add rejection reason if available
        if (it.rejection_reason) {
          transformed.rejection_reason = it.rejection_reason;
        }
        // Add rejection source (who rejected: me, both)
        if (it.rejectionSource) {
          transformed.rejectionSource = it.rejectionSource;
        }
        return transformed;
      });

      // Pending reviews already in desired shape
      setApprovedReviews(approvedArr);
      setRejectedReviews(rejectedArr);
      setPendingReviews(data.pending || []);
      setPricingRequestsProgress(data.pricingRequestsProgress || []);
      setRaRequestsProgress(data.raRequestsProgress || []);
      setSummary(data.summary || {
        approved_count: (data.approved || []).length,
        rejected_count: (data.rejected || []).length,
        pending_count: (data.pending || []).length,
        pricing_count: (data.pricingRequestsProgress || []).length,
        ra_count: (data.raRequestsProgress || []).length,
      });
    } catch (err) {
      setError(err);
      // In case of error, clear all lists
      setApprovedReviews([]);
      setRejectedReviews([]);
      setPendingReviews([]);
      setPricingRequestsProgress([]);
      setRaRequestsProgress([]);
      setSummary({ 
        approved_count: 0, 
        rejected_count: 0, 
        pending_count: 0,
        pricing_count: 0,
        ra_count: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    approvedReviews,
    rejectedReviews,
    pendingReviews,
    pricingRequestsProgress,
    raRequestsProgress,
    summary,
    loading,
    error,
    refetch: fetchData,
  };
};

export default useMyCommitteeReviews; 